import { useState } from "react";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Lock, Plus } from "lucide-react";
import { ManualEntryForm } from "./ManualEntryForm";
import { UploadEstimatesForm } from "./UploadEstimatesForm";
import { UploadSingleEstimateForm } from "./UploadSingleEstimateForm";
import { usePaymentMethodGate } from "@/hooks/use-subscription-guard";
import { PaywallModal } from "@/components/subscription/PaywallModal";

export function CreateClaimDialog() {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("single-upload");
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [, setLocation] = useLocation();

    // Running a comparison is pay-as-you-go: gate the tab until a card is on file.
    // usePaymentMethodGate only locks once we definitively know there's no payment
    // method (not while loading/errored), so paid users aren't falsely locked out.
    const { shouldPaywall: comparisonLocked } = usePaymentMethodGate();

    const handleTabChange = (value: string) => {
        // Intercept the locked comparison tab: show the paywall instead of switching.
        if (value === "comparison" && comparisonLocked) {
            setPaywallOpen(true);
            return;
        }
        setActiveTab(value);
    };

    const handleSuccess = (claimId?: number) => {
        setOpen(false);
        setActiveTab("single-upload"); // Reset to default

        if (claimId) {
            // Navigate to claim detail
            setLocation(`/claims/${claimId}`);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="w-full md:w-auto">
                    <Plus className="mr-2 h-4 w-4" />
                    New Claim
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Create New Claim</DialogTitle>
                    <DialogDescription>
                        Upload an estimate, run a comparison, or add a claim manually.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="single-upload" className="px-1 text-xs sm:px-3 sm:text-sm">
                            <span className="sm:hidden">Upload</span>
                            <span className="hidden sm:inline">Upload Estimate</span>
                        </TabsTrigger>
                        <TabsTrigger value="comparison" className="px-1 text-xs sm:px-3 sm:text-sm">
                            {comparisonLocked && <Lock className="mr-1 h-3 w-3 shrink-0" />}
                            <span className="sm:hidden">Compare</span>
                            <span className="hidden sm:inline">Run Comparison</span>
                        </TabsTrigger>
                        <TabsTrigger value="manual" className="px-1 text-xs sm:px-3 sm:text-sm">
                            <span className="sm:hidden">Manual</span>
                            <span className="hidden sm:inline">Manual Entry</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="single-upload" className="py-4">
                        <UploadSingleEstimateForm onSuccess={(claimId) => handleSuccess(claimId)} />
                    </TabsContent>

                    <TabsContent value="comparison" className="py-4">
                        <UploadEstimatesForm onSuccess={handleSuccess} />
                    </TabsContent>

                    <TabsContent value="manual" className="py-4">
                        <ManualEntryForm onSuccess={(claimId) => handleSuccess(claimId)} />
                    </TabsContent>
                </Tabs>

                <PaywallModal
                    open={paywallOpen}
                    onOpenChange={setPaywallOpen}
                    feature="comparison"
                />
            </DialogContent>
        </Dialog>
    );
}
